import { useState, useRef, useEffect, useMemo } from 'react';

import {
  FiMessageCircle,
  FiHelpCircle,
  FiGlobe,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { CgDice3, CgPoll } from 'react-icons/cg';
import { FiCloud } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { RoomSettingsData } from '@common/types/api';

import {
  Chat,
  Asking,
  Quiz,
  Poll,
  WordCloud,
  PublicQuestions,
} from '../../Interactions/tabs';
import * as S from './styles';

// Types
type TabId =
  | 'chat'
  | 'asking'
  | 'public_questions'
  | 'quiz'
  | 'poll'
  | 'word_cloud';

type Tab = {
  id: TabId;
  labelKey: string;
  icon: React.ElementType;
  disabled?: boolean;
};

// Tab Configuration - Base tabs with metadata (using i18n keys)
const TAB_CONFIG: Record<TabId, { labelKey: string; icon: React.ElementType }> =
  {
    chat: { labelKey: 'pages.broadcast.tabs.chat', icon: FiMessageCircle },
    asking: { labelKey: 'pages.broadcast.tabs.askings', icon: FiHelpCircle },
    public_questions: {
      labelKey: 'pages.broadcast.tabs.publicQuestions',
      icon: FiGlobe,
    },
    quiz: { labelKey: 'pages.broadcast.tabs.quiz', icon: CgDice3 },
    poll: { labelKey: 'pages.broadcast.tabs.poll', icon: CgPoll },
    word_cloud: { labelKey: 'pages.broadcast.tabs.cloud', icon: FiCloud },
  };

// Tab Content Components Map
const TAB_COMPONENTS: Record<TabId, React.ComponentType<{ active: boolean }>> =
  {
    chat: Chat,
    asking: Asking,
    public_questions: PublicQuestions,
    quiz: Quiz,
    poll: Poll,
    word_cloud: WordCloud,
  };

// Helper to check if a tab is enabled in settings
const isTabEnabled = (tabId: TabId, settings?: RoomSettingsData): boolean => {
  if (!settings) return true;

  const settingKey = tabId as keyof RoomSettingsData;
  const value = settings[settingKey];

  return typeof value === 'number' ? value === 1 : true;
};

// Helper to get visible and ordered tabs from settings
const getVisibleTabs = (settings?: RoomSettingsData): Tab[] => {
  if (!settings?.visible_interactions) {
    return Object.entries(TAB_CONFIG).map(([id, config]) => ({
      id: id as TabId,
      ...config,
      disabled: !isTabEnabled(id as TabId, settings),
    }));
  }

  const tabs: Tab[] = [];

  settings.visible_interactions
    .filter((interaction) => interaction.visible)
    .sort((a, b) => a.order - b.order)
    .forEach((interaction) => {
      const tabId = interaction.key as TabId;
      const config = TAB_CONFIG[tabId];
      if (config) {
        tabs.push({
          id: tabId,
          ...config,
          disabled: !isTabEnabled(tabId, settings),
        });
      }
    });

  return tabs;
};

// Helper to split tabs for display and dropdown
const getTabsConfig = (tabs: Tab[]) => {
  const hasExtraTabs = tabs.length > 5;
  const displayTabs = hasExtraTabs ? tabs.slice(0, 3) : tabs;
  const dropdownTabs = hasExtraTabs ? tabs.slice(3) : [];
  return { displayTabs, dropdownTabs };
};

// Custom hook for click outside
const useClickOutside = (
  ref: React.RefObject<HTMLElement>,
  callback: () => void,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, callback, enabled]);
};

// Custom hook for tab selection with automatic fallback
const useActiveTab = (visibleTabs: Tab[]) => {
  const [activeTab, setActiveTab] = useState<TabId | null>(null);

  useEffect(() => {
    if (visibleTabs.length === 0) {
      setActiveTab(null);
      return;
    }

    const currentTab = visibleTabs.find((tab) => tab.id === activeTab);
    const isCurrentTabValid = currentTab && !currentTab.disabled;

    // If no active tab or current tab is no longer valid, switch to first enabled
    if (!activeTab || !isCurrentTabValid) {
      const firstEnabledTab = visibleTabs.find((tab) => !tab.disabled);
      setActiveTab(firstEnabledTab?.id ?? visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  return [activeTab, setActiveTab] as const;
};

// Sub-components
type CollapsedTabsProps = {
  tabs: Tab[];
  activeTab: TabId;
  onTabSelect: (id: TabId) => void;
  t: TFunction;
};

const CollapsedTabs = ({
  tabs,
  activeTab,
  onTabSelect,
  t,
}: CollapsedTabsProps) => (
  <S.CollapsedTabs>
    {tabs.map((tab) => {
      const label = t(tab.labelKey);
      return (
        <S.CollapsedTab
          key={tab.id}
          $active={activeTab === tab.id}
          $disabled={tab.disabled}
          onClick={() => !tab.disabled && onTabSelect(tab.id)}
          type="button"
          title={
            tab.disabled
              ? `${label} (${t('pages.broadcast.interactions.disabled')})`
              : label
          }
        >
          <tab.icon size={20} />
        </S.CollapsedTab>
      );
    })}
  </S.CollapsedTabs>
);

type DropdownMenuProps = {
  tabs: Tab[];
  activeTab: TabId;
  onTabSelect: (id: TabId) => void;
  t: TFunction;
};

const DropdownMenu = ({
  tabs,
  activeTab,
  onTabSelect,
  t,
}: DropdownMenuProps) => (
  <S.Dropdown>
    {tabs.map((tab) => (
      <S.DropdownItem
        key={tab.id}
        $active={activeTab === tab.id}
        $disabled={tab.disabled}
        onClick={() => !tab.disabled && onTabSelect(tab.id)}
        type="button"
      >
        <tab.icon size={16} />
        <span>{t(tab.labelKey)}</span>
      </S.DropdownItem>
    ))}
  </S.Dropdown>
);

type MoreDropdownProps = {
  dropdownRef: React.RefObject<HTMLDivElement>;
  activeInDropdown: Tab | undefined;
  isOpen: boolean;
  onToggle: () => void;
  dropdownTabs: Tab[];
  activeTab: TabId;
  onTabSelect: (id: TabId) => void;
  t: TFunction;
};

const MoreDropdown = ({
  dropdownRef,
  activeInDropdown,
  isOpen,
  onToggle,
  dropdownTabs,
  activeTab,
  onTabSelect,
  t,
}: MoreDropdownProps) => (
  <S.MoreTab ref={dropdownRef} $active={!!activeInDropdown} onClick={onToggle}>
    {activeInDropdown ? (
      <>
        <activeInDropdown.icon size={16} />
        <span>{t(activeInDropdown.labelKey)}</span>
      </>
    ) : (
      <>
        <span>{t('pages.broadcast.tabs.more')}</span>
        <FiChevronDown size={14} />
      </>
    )}
    {isOpen && (
      <DropdownMenu
        tabs={dropdownTabs}
        activeTab={activeTab}
        onTabSelect={onTabSelect}
        t={t}
      />
    )}
  </S.MoreTab>
);

// Tab Content Renderer
const TabContent = ({
  activeTab,
  isDisabled,
  t,
}: {
  activeTab: TabId;
  isDisabled: boolean;
  t: TFunction;
}) => {
  if (isDisabled) {
    return (
      <S.DisabledContent>
        <span>{t('pages.broadcast.interactions.disabledMessage')}</span>
      </S.DisabledContent>
    );
  }

  const Component = TAB_COMPONENTS[activeTab];
  return <Component active />;
};

// Collapsed View Component
type CollapsedViewProps = {
  inline: boolean;
  onToggleCollapse?: () => void;
  visibleTabs: Tab[];
  activeTab: TabId;
  onTabSelect: (id: TabId) => void;
  t: TFunction;
};

const CollapsedView = ({
  inline,
  onToggleCollapse,
  visibleTabs,
  activeTab,
  onTabSelect,
  t,
}: CollapsedViewProps) => (
  <S.Container $collapsed $inline={inline}>
    {onToggleCollapse && (
      <S.HeaderWrapper>
        <S.ToggleButton onClick={onToggleCollapse} type="button">
          <FiChevronLeft size={14} />
        </S.ToggleButton>
        <S.CollapsedHeader />
      </S.HeaderWrapper>
    )}
    <CollapsedTabs
      tabs={visibleTabs}
      activeTab={activeTab}
      onTabSelect={onTabSelect}
      t={t}
    />
  </S.Container>
);

// Main Component
type InteractionPanelProps = {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onlineCount?: number;
  settings?: RoomSettingsData;
  inline?: boolean;
};

const InteractionPanel = ({
  collapsed = false,
  onToggleCollapse,
  onlineCount: _ignored = 0,
  settings,
  inline = false,
}: InteractionPanelProps) => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const visibleTabs = useMemo(() => getVisibleTabs(settings), [settings]);
  const [activeTab, setActiveTab] = useActiveTab(visibleTabs);

  const closeDropdown = () => setDropdownOpen(false);
  useClickOutside(dropdownRef, closeDropdown, dropdownOpen);

  const { displayTabs, dropdownTabs } = getTabsConfig(visibleTabs);
  const activeInDropdown = dropdownTabs.find((item) => item.id === activeTab);
  const activeTabData = visibleTabs.find((item) => item.id === activeTab);

  const handleTabSelect = (id: TabId) => {
    setActiveTab(id);
    setDropdownOpen(false);
  };

  if (visibleTabs.length === 0 || !activeTab) {
    return null;
  }

  if (collapsed && !inline) {
    return (
      <CollapsedView
        inline={inline}
        onToggleCollapse={onToggleCollapse}
        visibleTabs={visibleTabs}
        activeTab={activeTab}
        onTabSelect={handleTabSelect}
        t={t}
      />
    );
  }

  const activeTabLabel = activeTabData ? t(activeTabData.labelKey) : '';
  const headerTitle =
    activeTab === 'chat' ? t('pages.broadcast.chat.title') : activeTabLabel;
  const isActiveTabDisabled = activeTabData?.disabled ?? false;

  return (
    <S.Container $collapsed={false} $inline={inline}>
      <S.Content>
        <S.TabsBar>
          {displayTabs.map((tab) => {
            const label = t(tab.labelKey);
            return (
              <S.Tab
                key={tab.id}
                $active={activeTab === tab.id}
                $disabled={tab.disabled}
                onClick={() => !tab.disabled && handleTabSelect(tab.id)}
                type="button"
                title={
                  tab.disabled
                    ? `${label} (${t('pages.broadcast.interactions.disabled')})`
                    : undefined
                }
              >
                <tab.icon size={16} />
                <span>{label}</span>
              </S.Tab>
            );
          })}
          {dropdownTabs.length > 0 && (
            <MoreDropdown
              dropdownRef={dropdownRef}
              activeInDropdown={activeInDropdown}
              isOpen={dropdownOpen}
              onToggle={() => setDropdownOpen(!dropdownOpen)}
              dropdownTabs={dropdownTabs}
              activeTab={activeTab}
              onTabSelect={handleTabSelect}
              t={t}
            />
          )}
        </S.TabsBar>

        <S.HeaderWrapper>
          {onToggleCollapse && !inline && (
            <S.CollapseTab onClick={onToggleCollapse} type="button">
              <FiChevronRight size={14} />
            </S.CollapseTab>
          )}
          <S.Header>
            <S.HeaderTitle>{headerTitle}</S.HeaderTitle>
          </S.Header>
        </S.HeaderWrapper>

        <S.TabContentWrapper>
          <TabContent
            activeTab={activeTab}
            isDisabled={isActiveTabDisabled}
            t={t}
          />
        </S.TabContentWrapper>
      </S.Content>
    </S.Container>
  );
};

export default InteractionPanel;
